import { pick } from "lodash";
import qs from "query-string";

import { STATUS } from "@/utils/constants";

import * as api from "../../apiv2/views";
import * as alertActions from "../alerts/alerts.actions";
import { userSelector } from "../customers/customers.selectors";
import { handleError } from "../utils/error";

import {
  RESET,
  SET_ACTIVE_VIEW,
  SET_DEFAULT_VIEW,
  SET_IS_CREATE_VIEW_FORM_VISIBLE,
  SET_IS_FETCHING_STATUS,
  SET_IS_FETCHING_VIEWS,
  SET_SELECTED_VIEW,
  SET_VIEWS,
} from "./views.actionTypes";
import * as selectors from "./views.selectors";

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setViews = (data) => (dispatch) => {
  dispatch({
    type: SET_VIEWS,
    payload: data,
  });
};

export const setActiveView = (data) => (dispatch) => {
  dispatch({
    type: SET_ACTIVE_VIEW,
    payload: data,
  });
};

export const setIsFetchingViews = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_VIEWS,
    payload: status,
  });
};

export const setIsFetchingStatus = (status) => (dispatch) => {
  dispatch({ type: SET_IS_FETCHING_STATUS, payload: status });
};

export const setSelectedView = (status) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_VIEW,
    payload: status,
  });
};

export const setDefaultView = (status) => (dispatch) => {
  dispatch({
    type: SET_DEFAULT_VIEW,
    payload: status,
  });
};

export const setIsCreateViewFormVisible = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_CREATE_VIEW_FORM_VISIBLE,
    payload: status,
  });
};

export const fetchViews = (params) => async (dispatch, getState) => {
  try {
    const state = getState();
    const currentUser = userSelector(state);

    dispatch(setIsFetchingViews(STATUS.PENDING));

    const { data } = await api.getViews(params)();

    dispatch(setViews([{ uid: "none", name: "None" }, ...data]));

    dispatch(setIsFetchingStatus(STATUS.FULFILLED));

    // Optional-chained: the planner bootstrap can run this before userdata has resolved, and a
    // missing user must mean "no default view", not a crash routed to an error toast.
    const defaultViewUid = currentUser?.defaultCustomViews?.[params.page] ?? null;

    if (defaultViewUid !== null) {
      const selectedView = data.find((d) => d.uid === defaultViewUid);

      if (selectedView) {
        dispatch(setSelectedView(selectedView));
        dispatch(setDefaultView(selectedView));
        dispatch(setIsFetchingViews(STATUS.FULFILLED));
        return;
      }
    }

    dispatch(setSelectedView({ uid: "none", name: "None" }));
    dispatch(setDefaultView({ uid: "none", name: "None" }));
    dispatch(setIsFetchingViews(STATUS.FULFILLED));
  } catch (error) {
    dispatch(setIsFetchingViews(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};

export const saveDefaultView =
  ({ uid, page, isDefault = false } = {}) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const views = selectors.getViews(state);
      const view = views.find((v) => v.uid === uid);
      const defaultView = selectors.getDefaultView(state);
      const defaultViewUid = defaultView?.uid;
      // check if uid belongs to None option
      if (view && view.name === "None") {
        await api.postDefaultView({ uid: null, page })();
      } else {
        await api.postDefaultView({ uid, page })();
      }

      // if the default view is not the same as the view being edited
      // set the default view to the new view
      if (uid !== defaultViewUid) {
        dispatch(setDefaultView(view));
      }

      // if the selected view and the view being editted is being saved as not the default view
      // set the default view to none
      if (uid === defaultViewUid && !isDefault) {
        dispatch(setDefaultView({ uid: "none", name: "None" }));
      }
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const updateView =
  ({ uid, payload }) =>
  async (dispatch, getState) => {
    const state = getState();
    const selectedView = selectors.getSelectedView(state);
    const selectedViewUid = selectedView?.uid;
    const activeView = selectors.getActiveView(state);
    const defaultView = selectors.getDefaultView(state);

    const views = selectors.getViews(state);
    const { data } = await api.patchView({
      uid,
      payload,
    })();

    // Whether the view being edited was the default BEFORE this save. Decides below whether a
    // toggle-off save is "clear the default" or "the default was never involved".
    const wasDefault = defaultView?.uid === uid;

    // selectedView (the toolbar pill) and defaultView (the row list, via getFormattedViews) each
    // hold their own snapshot of the view object, so an update must be written into them —
    // setViews below only refreshes the views array, and a stale name would stick until a page
    // refresh. Selection: refresh it when the updated view is the selected one, and move it here
    // when the view was just made default from elsewhere (pre-existing behaviour). Saving the
    // selected view without the default toggle used to kick the selection to None; a plain
    // rename must not deselect the view the user is looking at.
    if (selectedViewUid === uid || payload.is_default) {
      dispatch(setSelectedView(data));
    }
    if (payload.is_default) {
      dispatch(setDefaultView(data));
    }

    dispatch(setViews(views.map((v) => (v.uid === uid ? { ...data } : { ...v }))));

    // Only touch the stored default when this edit involves it: the toggle on moves the default
    // to this view, and the toggle off on the view that IS the default clears it. Editing any
    // other view without the toggle leaves the default alone — this used to post null
    // unconditionally, silently wiping whatever default the user had.
    if (activeView && payload.is_default) {
      await dispatch(saveDefaultView({ uid: data.uid, page: payload.page, isDefault: true }));
    } else if (activeView && wasDefault) {
      await dispatch(saveDefaultView({ uid: "none", page: payload.page, isDefault: false }));
    }

    return data;
  };

export const createView =
  ({ payload }) =>
  async (dispatch, getState) => {
    const state = getState();
    const views = selectors.getViews(state);

    const { data } = await api.postView(payload)();
    dispatch(setViews([...views, data]));

    if (payload.is_default) {
      await dispatch(saveDefaultView({ uid: data.uid, page: payload.page }));
    }

    return data;
  };

export const saveView =
  ({ payload }) =>
  async (dispatch, getState) => {
    const state = getState();
    const activeView = selectors.getActiveView(state);
    const views = selectors.getViews(state);
    const noneView = views.find((v) => v.name === "None");

    const customFilter =
      payload.custom_filter !== ""
        ? qs.stringify(
            pick(qs.parse(payload.custom_filter), [
              "assignees",
              "labels",
              "sort_by",
              "resources",
              "columns",
              "search",
              "is_scheduled",
              "statuses",
              "calendar_view",
              "required_start_date",
              "required_end_date",
              "show_child_cards",
              // Planner keys. `pick` is the only gate between a builder and storage, so a key
              // missing here is silently dropped on every save.
              "progress",
              "colour_by",
              "display",
              "schedule_state",
            ])
          )
        : payload.customFilter;

    try {
      dispatch(setIsCreateViewFormVisible(false));

      if (payload.name === "None" && noneView) {
        dispatch(setSelectedView(noneView));
        await dispatch(saveDefaultView({ uid: noneView.uid, page: payload.page }));

        return noneView;
      }

      const data = activeView
        ? await dispatch(
            updateView({
              uid: activeView.uid,
              payload: { ...payload, custom_filter: customFilter },
            })
          )
        : await dispatch(createView({ payload: { ...payload, custom_filter: customFilter } }));

      return data;
    } catch (error) {
      console.log("error", error);
      handleError(error, dispatch);
    }
  };

// `page` is passed in rather than read off the router: it used to be the pathname with the
// leading slash stripped, so visiting /planner/ sent page="planner/" and the backend rejected the
// whole call, stranding the stored default on a view that no longer exists.
export const removeView = (uid, page) => async (dispatch, getState) => {
  try {
    const state = getState();
    const views = selectors.getViews(state);
    const selectedView = selectors.getSelectedView(state);
    const defaultView = selectors.getDefaultView(state);
    const noneView = views.find((v) => v.name === "None");

    const wasSelected = selectedView?.uid === uid;
    const wasDefault = defaultView?.uid === uid;

    await api.deleteView(uid)();
    dispatch(setViews(views.filter((v) => v.uid !== uid)));
    dispatch(alertActions.createAlertAction(uid, "View Deleted", true, "danger"));
    dispatch(setActiveView(null));
    dispatch(setIsCreateViewFormVisible(false));

    // Deleting the view you are in lands you on your default, or on None when the default is the
    // one that just went.
    if (wasSelected) {
      dispatch(setSelectedView(wasDefault ? noneView : defaultView));
    }

    // The stored default is stale whenever the deleted view was the default, whatever happens to
    // be selected. Gating this on the selection is what left a dead uid in the user's
    // default_custom_views forever.
    if (wasDefault) {
      dispatch(setDefaultView(noneView));
      await dispatch(saveDefaultView({ uid: noneView.uid, page }));
    }
  } catch (error) {
    handleError(error, dispatch);
  }
};

// export const getView = (uid) => async (dispatch) => {
//   try {
//     if (uid) {
//       const { data } = await api.getView(uid)();
//       dispatch(setDefaultView(data));
//     } else {
//       dispatch(setDefaultView(view));
//     }
//   } catch (error) {
//     handleError(error, dispatch);
//   }
// };
